import React, { useState } from "react";
import { Upload, CheckCircle2, XCircle, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const downloadCsv = (name, header, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export const CsvImportDialog = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [aiCheck, setAiCheck] = useState(true);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [includeFlagged, setIncludeFlagged] = useState(false);

  const reset = () => { setFile(null); setReport(null); setIncludeFlagged(false); };

  const validate = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/events/import/validate?ai_check=${aiCheck}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setReport(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Validation failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/events/import/commit", { token: report.token, include_ai_flagged: includeFlagged });
      toast.success(`Imported ${r.data.inserted} events${r.data.skipped ? ` (${r.data.skipped} skipped)` : ""}`);
      onImported?.(); reset(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  const exportRejected = () => downloadCsv(`rejected-rows-${file?.name || "import"}.csv`, ["Row", "Reference_ID", "Date_DD-MM", "Year", "Event_Title", "Problems"],
    report.rejected.map((r) => [r.row, r.reference_id, r.date_dd_mm, r.year, r.event_title, r.problems.join(" | ")]));

  const importable = report ? report.accepted_count - (includeFlagged ? 0 : report.ai_flagged_count) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="csv-import-dialog">
        <DialogHeader><DialogTitle className="font-display text-ink">Upload events CSV</DialogTitle></DialogHeader>
        {!report ? (
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">Columns (DD-MM dates): <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">Reference_ID, Date_DD-MM, Year, Category, Event_Title, Description, Source_Primary, Source_Secondary, Copyright_Status</code>. Nothing is saved until you review the validation report.</p>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-2xl p-8 cursor-pointer hover:border-gold" data-testid="csv-dropzone">
              <Upload className="w-6 h-6 text-gold" />
              <span className="text-sm text-ink">{file ? file.name : "Choose a .csv file"}</span>
              <input data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink"><input data-testid="csv-ai-check" type="checkbox" checked={aiCheck} onChange={(e) => setAiCheck(e.target.checked)} className="accent-gold w-4 h-4" /> AI fact-check year &amp; date vs title (first 60 accepted rows)</label>
            <div className="flex justify-end">
              <button data-testid="csv-validate" disabled={!file || busy} onClick={validate} className="px-5 h-10 rounded-full bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink disabled:opacity-60">{busy ? "Validating…" : "Validate"}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="csv-report">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-xl border border-slate-200 p-3"><p className="text-2xl font-extrabold text-ink" data-testid="csv-accepted">{report.accepted_count}</p><p className="font-mono text-[10px] uppercase text-ink-muted">accepted</p></div>
              <div className="rounded-xl border border-slate-200 p-3"><p className="text-2xl font-extrabold text-red-600" data-testid="csv-rejected">{report.rejected_count}</p><p className="font-mono text-[10px] uppercase text-ink-muted">rejected</p></div>
              <div className="rounded-xl border border-slate-200 p-3"><p className="text-2xl font-extrabold text-gold" data-testid="csv-flagged">{report.ai_flagged_count}</p><p className="font-mono text-[10px] uppercase text-ink-muted">AI flagged</p></div>
            </div>
            {report.rejected.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1"><p className="text-sm font-semibold text-ink flex items-center gap-1.5"><XCircle className="w-4 h-4 text-red-600" /> Rejected rows (not imported)</p><button onClick={exportRejected} data-testid="csv-export-rejected" className="text-xs font-mono text-ink-muted hover:text-gold inline-flex items-center gap-1"><Download className="w-3 h-3" /> CSV</button></div>
                <ul className="max-h-40 overflow-auto text-xs font-mono divide-y divide-slate-100 border border-slate-200 rounded-lg">
                  {report.rejected.map((r) => <li key={r.row} className="px-3 py-1.5"><span className="text-ink-muted">row {r.row}</span> <span className="text-ink">{r.event_title || r.reference_id}</span> — <span className="text-red-600">{r.problems.join("; ")}</span></li>)}
                </ul>
              </div>
            )}
            {report.ai_flags.filter((f) => f.row).length > 0 && (
              <div>
                <p className="text-sm font-semibold text-ink flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4 text-gold" /> AI fact-check flags (held back unless you include them)</p>
                <ul className="max-h-32 overflow-auto text-xs font-mono divide-y divide-slate-100 border border-gold/50 rounded-lg bg-paper">
                  {report.ai_flags.filter((f) => f.row).map((f) => <li key={f.row} className="px-3 py-1.5"><span className="text-ink-muted">row {f.row}</span> <span className="text-ink font-semibold">{f.verdict}</span> — {f.reason}</li>)}
                </ul>
                <label className="mt-2 flex items-center gap-2 text-sm text-ink"><input data-testid="csv-include-flagged" type="checkbox" checked={includeFlagged} onChange={(e) => setIncludeFlagged(e.target.checked)} className="accent-gold w-4 h-4" /> Import flagged rows anyway</label>
              </div>
            )}
            {report.warnings.length > 0 && (
              <details className="text-xs font-mono text-ink-muted"><summary className="cursor-pointer">{report.warnings.length} warnings (imported, but check)</summary>
                <ul className="mt-1 max-h-32 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">{report.warnings.map((w) => <li key={w.row} className="px-3 py-1.5">row {w.row} {w.reference_id}: {w.notes.join("; ")}</li>)}</ul>
              </details>
            )}
            <div className="flex items-center justify-between pt-2">
              <button onClick={reset} className="text-sm text-ink-muted hover:text-gold">← Choose another file</button>
              <button data-testid="csv-commit" disabled={busy || importable === 0} onClick={commit} className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-gold text-ink text-sm font-semibold hover:bg-[#c19614] disabled:opacity-60"><CheckCircle2 className="w-4 h-4" /> {busy ? "Importing…" : `Import ${importable} events`}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
